// ============================================================
// Offline inspection queue (Brief v2 · Task 5).
//
// Inspectors lose signal on site constantly. The engine already keeps the
// full project state in localStorage, so we never *lose* an edit — but a
// failed sync used to vanish silently. This module makes the pending sync
// DURABLE and OBSERVABLE:
//
//   * persist the latest unsynced snapshot per project in IndexedDB
//     (survives a tab close / crash, unlike an in-memory retry);
//   * expose a pending count so the header can show "Queued (N)";
//   * flush automatically when connectivity returns (online event +
//     a periodic re-check), replaying through replace_project_state.
//
// Because replace_project_state is a full idempotent replace, we only need
// to keep the MOST RECENT snapshot per project — newer supersedes older —
// so the "queue" is one row per project with unsynced work.
// ============================================================
import { supabase } from "./supabase";

const DB_NAME = "gridproof-offline";
const STORE = "queue";

interface QueuedSnap {
  projectId: string;
  snapshot: unknown;
  queuedAt: number;
}

let _db: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  if (_db) return _db;
  _db = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains(STORE)) {
        d.createObjectStore(STORE, { keyPath: "projectId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _db;
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return db().then(
    (d) =>
      new Promise<T>((resolve, reject) => {
        const t = d.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

// ---- observers (header badge) ----------------------------------------------
const listeners = new Set<(count: number) => void>();
let _count = 0;

export function subscribeQueue(cb: (count: number) => void): () => void {
  listeners.add(cb);
  cb(_count);
  return () => listeners.delete(cb);
}

async function refreshCount(): Promise<number> {
  try {
    _count = await tx<number>("readonly", (s) => s.count());
  } catch {
    _count = 0;
  }
  listeners.forEach((l) => l(_count));
  return _count;
}

export function pendingCount(): number {
  return _count;
}

/** Persist the latest unsynced snapshot for a project (supersedes any older). */
export async function enqueueSnapshot(projectId: string, snapshot: unknown): Promise<void> {
  const row: QueuedSnap = { projectId, snapshot, queuedAt: Date.now() };
  await tx("readwrite", (s) => s.put(row));
  await refreshCount();
}

let _flushing = false;

/** Replay every queued snapshot through the RPC. Safe to call repeatedly. */
export async function flushQueue(): Promise<void> {
  if (_flushing || !supabase) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  _flushing = true;
  try {
    const rows = await tx<QueuedSnap[]>("readonly", (s) => s.getAll());
    for (const row of rows) {
      try {
        await supabase.rpc("replace_project_state", {
          p_project: row.projectId,
          snap: row.snapshot,
        });
        await tx("readwrite", (s) => s.delete(row.projectId));
      } catch {
        // Still offline / failing — leave it queued for the next attempt.
      }
    }
  } finally {
    _flushing = false;
    await refreshCount();
  }
}

let _started = false;
/** Wire connectivity listeners + an initial flush. Call once at boot. */
export function initOfflineQueue(): void {
  if (_started || typeof window === "undefined") return;
  _started = true;
  window.addEventListener("online", () => void flushQueue());
  // Periodic safety net for flaky links where the `online` event never fires.
  window.setInterval(() => void flushQueue(), 30_000);
  void refreshCount().then(() => flushQueue());
}
